/**
 * The game-entry screens: Register / Login / Google / Guest, plus the
 * "forgot password" and "choose a commander name" steps. Rendered by
 * `AuthGate` in place of the game whenever there is no Supabase session yet
 * (apps/web/src/game/AuthGate.tsx).
 *
 * Every "founding a colony" path (Register, Guest) ends in `onNamed`, so the
 * player is asked for a commander name exactly once. Login and Google skip
 * it — a returning identity already has a name (or the "Kommandant" default,
 * editable later in Settings). `RecoveryScreen` is exported separately and
 * rendered directly by `AuthGate` — it isn't part of this state machine
 * because it's reached via a password-reset email link, not a click here.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Button, TextField, cn } from '@spacegame/ui';
import { entryHero, entryCommander } from '@spacegame/assets/entry';
import type { SupabaseAuth } from '../game/supabase-auth';
import { useActivityFeed, useOnlineCount } from '../game/useActivity';
import { useSettings } from '../game/SettingsProvider';

type Screen = 'start' | 'register' | 'registerName' | 'login' | 'forgot' | 'guestName';

export interface EntryViewProps {
  auth: SupabaseAuth;
  /** Called once a commander name is chosen (Register or Guest path) or immediately after Login/Google. */
  onEnter: (pendingName: string | null) => void;
  logo?: ReactNode;
}

const MIN_PASSWORD_LENGTH = 8;

function EntryShell({ children }: { children: ReactNode }) {
  return (
    <div className="sg-root relative flex min-h-screen items-center justify-center overflow-hidden bg-surface-base p-4">
      <img
        src={entryHero}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover opacity-90"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-surface-base/95 via-surface-base/25 to-surface-base/40"
      />
      <div className="sg-scanline pointer-events-none absolute inset-0" aria-hidden />
      <div className="relative w-full max-w-sm rounded-panel border border-edge-strong bg-plate-raised p-6 shadow-panel-lg">
        {children}
      </div>
    </div>
  );
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const TICKER_ROTATE_MS = 6_000;

/**
 * Live-ticker / online-count (galaxy plan Milestone D UI). Deliberately
 * modest for a first-impression, conversion-critical screen with limited
 * mobile height: one static status line, one rotating (not scrolling —
 * easier to keep accessible, see reducedMotion below) recent-activity
 * message. Renders nothing at all until real data exists — a "0 Piloten
 * aktiv" or an empty ticker would look broken, worse than absent.
 */
function ActivityTicker() {
  const onlineCount = useOnlineCount();
  const feed = useActivityFeed(8);
  const settings = useSettings();
  const [index, setIndex] = useState(0);
  const rotate = !settings.reducedMotion && feed.length > 1;

  useEffect(() => {
    if (!rotate) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % feed.length), TICKER_ROTATE_MS);
    return () => clearInterval(id);
  }, [rotate, feed.length]);

  if (!onlineCount && feed.length === 0) return null;

  const current = feed.length > 0 ? feed[index % feed.length] : null;

  return (
    <div className="mt-4 border-t border-edge-subtle pt-3 text-center">
      {onlineCount ? (
        <p className="flex items-center justify-center gap-1.5 text-[11px] font-medium text-content-secondary">
          <span
            aria-hidden
            className={cn('h-1.5 w-1.5 rounded-full bg-success', !settings.reducedMotion && 'animate-pulse-glow')}
          />
          {onlineCount.toLocaleString('de-DE')} Piloten aktiv
        </p>
      ) : null}
      {current ? <p className="mt-1 truncate text-[11px] text-content-muted">{current.message}</p> : null}
    </div>
  );
}

export function EntryView({ auth, onEnter, logo }: EntryViewProps) {
  const [screen, setScreen] = useState<Screen>('start');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  /** Register succeeded but email confirmation is required — no session yet, no name step. */
  const [registerNeedsConfirmation, setRegisterNeedsConfirmation] = useState(false);

  const goTo = (next: Screen) => {
    setError(null);
    setFieldErrors({});
    setScreen(next);
  };

  const submitRegister = async (e: FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!isValidEmail(email)) errors.email = 'Bitte eine gültige E-Mail-Adresse eingeben.';
    if (password.length < MIN_PASSWORD_LENGTH) errors.password = `Mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`;
    if (password !== passwordConfirm) errors.passwordConfirm = 'Passwörter stimmen nicht überein.';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await auth.signUp(email, password);
      const session = await auth.peekSession();
      if (session) {
        goTo('registerName');
      } else {
        setRegisterNeedsConfirmation(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Registrierung fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitLogin = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await auth.signInWithPassword(email, password);
      onEnter(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await auth.resetPasswordForEmail(email);
    } catch {
      // Never reveal whether the account exists — same confirmation either way.
    } finally {
      setSubmitting(false);
      setForgotSent(true);
    }
  };

  const submitGoogle = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await auth.signInWithGoogle();
      // Redirects the browser away — nothing left to do here on success.
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Google-Anmeldung fehlgeschlagen.');
      setSubmitting(false);
    }
  };

  const submitName = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 20) {
      setFieldErrors({ name: 'Name muss 2–20 Zeichen lang sein.' });
      return;
    }
    onEnter(trimmed);
  };

  if (screen === 'start') {
    return (
      <EntryShell>
        {logo ? <div className="mb-4 flex justify-center">{logo}</div> : null}
        <p className="mb-5 text-center text-xs leading-relaxed text-content-muted">
          Baue deine Kolonie auf, verteidige sie und errichte die Dyson-Sphäre.
        </p>
        <div className="flex flex-col gap-2">
          <Button variant="primary" size="lg" fullWidth onClick={() => goTo('register')}>
            Registrieren
          </Button>
          <Button variant="secondary" size="lg" fullWidth onClick={() => goTo('login')}>
            Anmelden
          </Button>
          <Button variant="secondary" size="lg" fullWidth loading={submitting} onClick={submitGoogle}>
            Mit Google anmelden
          </Button>
        </div>
        {error ? <p className="mt-3 text-center text-xs text-danger-bright">{error}</p> : null}
        <div className="my-5 border-t border-edge-subtle" />
        <button
          type="button"
          onClick={() => goTo('guestName')}
          className="block w-full text-center text-xs font-medium text-content-secondary underline-offset-2 hover:text-content hover:underline"
        >
          Ohne Konto spielen
        </button>
        <p className="mt-1.5 text-center text-[11px] leading-relaxed text-content-muted">
          Spielstand nur auf diesem Gerät — später jederzeit in den Einstellungen sicherbar.
        </p>
        <ActivityTicker />
      </EntryShell>
    );
  }

  if (screen === 'register') {
    if (registerNeedsConfirmation) {
      return (
        <EntryShell>
          <p className="text-center text-sm font-medium text-content">Bestätige deine E-Mail</p>
          <p className="mt-2 text-center text-xs leading-relaxed text-content-muted">
            Wir haben dir eine Bestätigungs-E-Mail geschickt. Klicke auf den Link darin, dann kannst
            du dich normal anmelden.
          </p>
          <Button
            variant="ghost"
            fullWidth
            className="mt-4"
            onClick={() => {
              setRegisterNeedsConfirmation(false);
              goTo('login');
            }}
          >
            Zurück zum Login
          </Button>
        </EntryShell>
      );
    }
    return (
      <EntryShell>
        <h1 className="mb-4 text-center font-display text-sm font-semibold uppercase tracking-[0.12em] text-content">
          Konto erstellen
        </h1>
        <form className="flex flex-col gap-3" onSubmit={submitRegister}>
          <TextField
            label="E-Mail"
            type="email"
            autoComplete="email"
            value={email}
            onChange={setEmail}
            error={fieldErrors.email}
          />
          <TextField
            label="Passwort"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            error={fieldErrors.password}
          />
          <TextField
            label="Passwort bestätigen"
            type="password"
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={setPasswordConfirm}
            error={fieldErrors.passwordConfirm}
          />
          {error ? <p className="text-xs text-danger-bright">{error}</p> : null}
          <Button type="submit" variant="primary" fullWidth loading={submitting}>
            Registrieren
          </Button>
        </form>
        <Button variant="ghost" fullWidth className="mt-2" onClick={() => goTo('start')}>
          Zurück
        </Button>
      </EntryShell>
    );
  }

  if (screen === 'registerName' || screen === 'guestName') {
    return (
      <EntryShell>
        <img
          src={entryCommander}
          alt=""
          aria-hidden
          className="mx-auto mb-4 h-24 w-24 rounded-control object-cover"
        />
        <h1 className="mb-1 text-center font-display text-sm font-semibold uppercase tracking-[0.12em] text-content">
          Wie lautet dein Kommandantenname?
        </h1>
        <p className="mb-4 text-center text-xs leading-relaxed text-content-muted">
          Erscheint im Spiel und lässt sich später in den Einstellungen ändern.
        </p>
        <form className="flex flex-col gap-3" onSubmit={submitName}>
          <TextField
            label="Kommandantenname"
            value={name}
            onChange={setName}
            error={fieldErrors.name}
            maxLength={20}
            autoFocus
          />
          <Button type="submit" variant="primary" fullWidth>
            Kolonie gründen
          </Button>
        </form>
      </EntryShell>
    );
  }

  if (screen === 'login') {
    return (
      <EntryShell>
        <h1 className="mb-4 text-center font-display text-sm font-semibold uppercase tracking-[0.12em] text-content">
          Anmelden
        </h1>
        <form className="flex flex-col gap-3" onSubmit={submitLogin}>
          <TextField label="E-Mail" type="email" autoComplete="email" value={email} onChange={setEmail} />
          <TextField
            label="Passwort"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
          {error ? <p className="text-xs text-danger-bright">{error}</p> : null}
          <button
            type="button"
            onClick={() => goTo('forgot')}
            className="self-start text-xs text-content-secondary underline-offset-2 hover:text-content hover:underline"
          >
            Passwort vergessen?
          </button>
          <Button type="submit" variant="primary" fullWidth loading={submitting}>
            Anmelden
          </Button>
        </form>
        <Button variant="ghost" fullWidth className="mt-2" onClick={() => goTo('start')}>
          Zurück
        </Button>
      </EntryShell>
    );
  }

  // forgot
  return (
    <EntryShell>
      <h1 className="mb-4 text-center font-display text-sm font-semibold uppercase tracking-[0.12em] text-content">
        Passwort vergessen
      </h1>
      {forgotSent ? (
        <p className="text-center text-xs leading-relaxed text-content-muted">
          Falls ein Konto mit dieser E-Mail existiert, wurde eine E-Mail mit einem Link zum
          Zurücksetzen verschickt.
        </p>
      ) : (
        <form className="flex flex-col gap-3" onSubmit={submitForgot}>
          <TextField label="E-Mail" type="email" autoComplete="email" value={email} onChange={setEmail} />
          <Button type="submit" variant="primary" fullWidth loading={submitting}>
            Link senden
          </Button>
        </form>
      )}
      <Button variant="ghost" fullWidth className="mt-2" onClick={() => goTo('login')}>
        Zurück zum Login
      </Button>
    </EntryShell>
  );
}

export interface RecoveryScreenProps {
  auth: SupabaseAuth;
  onDone: () => void;
}

/** Reached via a password-reset email link — `AuthGate` detects the `PASSWORD_RECOVERY` auth event and renders this instead of the normal gate/game. */
export function RecoveryScreen({ auth, onDone }: RecoveryScreenProps) {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (password.length < MIN_PASSWORD_LENGTH) errors.password = `Mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`;
    if (password !== passwordConfirm) errors.passwordConfirm = 'Passwörter stimmen nicht überein.';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await auth.setNewPassword(password);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Passwort setzen fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EntryShell>
      <h1 className="mb-4 text-center font-display text-sm font-semibold uppercase tracking-[0.12em] text-content">
        Neues Passwort setzen
      </h1>
      <form className="flex flex-col gap-3" onSubmit={submit}>
        <TextField
          label="Neues Passwort"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          error={fieldErrors.password}
        />
        <TextField
          label="Passwort bestätigen"
          type="password"
          autoComplete="new-password"
          value={passwordConfirm}
          onChange={setPasswordConfirm}
          error={fieldErrors.passwordConfirm}
        />
        {error ? <p className="text-xs text-danger-bright">{error}</p> : null}
        <Button type="submit" variant="primary" fullWidth loading={submitting}>
          Passwort speichern
        </Button>
      </form>
    </EntryShell>
  );
}
